/* eslint-disable max-lines */
import { defineStore } from 'pinia';
import type { PiniaCustomProperties } from 'pinia';
import { computed, onUnmounted, reactive, ref, watch } from 'vue';
import type { ComputedRef, Ref } from 'vue';
import { ResponseError } from './apis/generated/common/runtime';
import type { ApiResponse } from './apis/generated/common/runtime';
import type { STORE } from './store-ids';
import debounce from 'p-debounce';
import { useApis } from './apis';
import type { PowerDialerApis } from './apis';
import { usePubSub } from './pub-sub';
import type { PubSub } from './pub-sub';
import logger from '@/logger';
import { exponentialRetry } from './utils/debounced-retry';
import { isRetryError } from './campaign/utils/validation';

const logFactory = logger.getChild('api-store-factory');

const ABORTED_ERROR = 'The operation was aborted.';

export type ApiEntity = {};

export type StoreEntity<
  T extends ApiEntity,
  EntityKey extends keyof T,
  TFilter extends GetFilter,
  CreateInput = T,
  UpdateInput = T,
> = {
  entity: T;
  id: EntityKey;
  filter: TFilter;
  createInput: CreateInput;
  updateInput: UpdateInput;
};

export type GetFilter = {};

export type ResponseCallbacks<R> = {
  /**
   * Called every time when retry error happens, HTTP status code >= 500
   * @param error - ResponseError
   * @returns
   */
  onRetry?: (error: ResponseError) => void | Promise<void>;
  /**
   * Called one time only when request fails with unrecoverable error
   * @param error
   * @returns
   */
  onError?: (error: unknown) => void | Promise<void>;
  /**
   * Called one time only when request is successful
   * @param response
   * @returns
   */
  onSuccess?: (response: R) => void | Promise<void>;
};

export enum REQUEST_POLICY {
  CACHE_FIRST = 'cache-first',
  CACHE_AND_NETWORK = 'cache-and-network',
  NETWORK_ONLY = 'network-only',
}

type RequiredApiEntityId<
  T extends ApiEntity,
  EntityKey extends keyof T,
> = Exclude<T[EntityKey], undefined>;

type ApiStoreState<TFilter extends GetFilter> = {
  filter: Ref<RemovePagination<TFilter> | null>;
  page: Ref<number>;
};

type ApiStoreGetters<T extends ApiEntity, EntityKey extends keyof T> = {
  cursor: ComputedRef<string | null>;
  data: ComputedRef<T[]>;
  loading: ComputedRef<boolean>;
  error: ComputedRef<ResponseError | null>;
  cache: ComputedRef<Map<RequiredApiEntityId<T, EntityKey>, T>>;
  total: ComputedRef<number>;
  watcherMemory: ComputedRef<
    Map<RequiredApiEntityId<T, EntityKey>, Watcher<T>>
  >;
  hasEntities: ComputedRef<boolean>;
};

const notImplemented = async () => {
  throw new Error('Not implemented');
};

type ApiStoreConfig<EntityKey> = {
  entityIdKey: EntityKey;
  supportPagination?: boolean;
  debounceTimeout?: number;
};

export type WatchFunctionReturn<T extends ApiEntity> = Ref<WatchedEntity<T>>;

export type WatchedEntity<T extends ApiEntity> = {
  item: T | null;
  loading: boolean;
  error: ResponseError | null;
};

type WatchFunction<T extends ApiEntity, EntityKey extends keyof T> = (
  getId: () => RequiredApiEntityId<T, EntityKey> | null,
  componentName: string,
) => WatchFunctionReturn<T>;

export type WatchManyFunctionReturn<T extends ApiEntity> = Ref<
  WatchedEntity<T>[]
>;

type WatchManyFunction<T extends ApiEntity, EntityKey extends keyof T> = (
  getIds: () => RequiredApiEntityId<T, EntityKey>[],
  componentName: string,
) => WatchManyFunctionReturn<T>;

type CancelRetryCallback = () => void;

type ApiStoreActions<
  T extends ApiEntity,
  EntityKey extends keyof T,
  CreateInput = T,
  UpdateInput = T,
> = {
  /**
   * Refresh search with the current filter
   * @param silent - silent update does not trigger loading (default: true)
   * @param requestTotal - request total number of entities for pagination (default: false)
   * @param resetHasEntities - recalculate hasEntities flag (default: false)
   */
  refresh: (
    silent?: boolean,
    requestTotal?: boolean,
    resetHasEntities?: boolean,
  ) => Promise<void>;

  /**
   * Load next page for infinite scroll
   */
  loadMore: () => Promise<void>;

  /**
   * Create entity, has integrated retry in case of recoverable errors
   * @param data - Data to create, defined during store creation
   * @param callbacks - Optional callbacks for success, error and retry
   * @param fetchAll - Whether to re-fetch all entities after success with current filter, default to true
   * @returns cancel callback
   */
  create: (
    data: CreateInput,
    callbacks?: ResponseCallbacks<T>,
    fetchAll?: boolean,
  ) => CancelRetryCallback;

  /**
   * Request entity, has integrated retry in case of recoverable errors.
   * Rejects only in case of unrecoverable errors
   * @param id - Entity id to read
   * @param requestPolicy - Request policy
   * @returns promisified entity
   */
  read: (
    id: RequiredApiEntityId<T, EntityKey>,
    requestPolicy?: REQUEST_POLICY,
  ) => Promise<T>;

  /**
   * Update entity, has integrated retry in case of recoverable errors
   * @param id - Entity id to update, defined during store creation
   * @param data - Data to update, defined during store creation
   * @param callbacks - Optional callbacks for success, error and retry
   * @returns cancel callback
   */
  update: (
    id: RequiredApiEntityId<T, EntityKey>,
    data: UpdateInput,
    callbacks?: ResponseCallbacks<T>,
  ) => CancelRetryCallback;

  /**
   * Discard entity, has integrated retry in case of recoverable errors
   * @param id - Entity id to discard, defined during store creation
   * @param callbacks - Optional callbacks for success, error and retry
   * @returns cancel callback
   */
  discard: (
    id: RequiredApiEntityId<T, EntityKey>,
    callbacks?: ResponseCallbacks<void>,
  ) => CancelRetryCallback;

  /**
   * Duplicate entity, has integrated retry in case of recoverable errors
   * @param data - Data to create, defined during store creation
   * @param callbacks - Optional callbacks for success, error and retry
   * @param fetchAll - Whether to re-fetch all entities after success with current filter, default to true
   * @returns cancel callback
   */
  duplicate: (
    id: RequiredApiEntityId<T, EntityKey>,
    callbacks?: ResponseCallbacks<T>,
    fetchAll?: boolean,
  ) => CancelRetryCallback;

  /**
   * Clear the store, removing all data
   */
  clear: () => void;

  /**
   * Clear global errors for search
   */
  clearError: () => void;

  /**
   * Remove entity from cache / clear entire cache for the store if id is not provided
   * @param id - optional entity id
   */
  clearCache: (id?: RequiredApiEntityId<T, EntityKey>) => void;

  /**
   * Watch an entity, returns vue ref that is connected to all updates
   * Utilize read to retrieve the entity if it is not in the cache
   *
   * Id getter is watched, and changes the entity in the same ref that was returned initially
   */
  watch: WatchFunction<T, EntityKey>;

  /**
   * Watch many entities, returns ref array with ref to each entity
   * Utilize read to retrieve the entity if it is not in the cache
   *
   * Id[] getter is watched, and changes the entity in the same ref that was returned initially
   */
  watchMany: WatchManyFunction<T, EntityKey>;
};

type ApiStoreFactoryApi = PowerDialerApis & { pubSub: PubSub };

type ApiStoreSetup<
  T extends ApiEntity,
  TFilter extends GetFilter,
  EntityKey extends keyof T,
  CreateInput = T,
  UpdateInput = T,
> = ApiStoreState<TFilter> &
  ApiStoreGetters<T, EntityKey> &
  ApiStoreActions<T, EntityKey, CreateInput, UpdateInput> &
  PiniaCustomProperties;

type ApiCRUDOperations<
  T extends ApiEntity,
  TFilter extends GetFilter,
  EntityKey extends keyof T,
  CreateInput = T,
  UpdateInput = T,
> = {
  create?: (data: CreateInput, apis: ApiStoreFactoryApi) => Promise<T>;
  readAll?: (
    filter: TFilter,
    apis: ApiStoreFactoryApi,
    signal?: AbortSignal,
  ) => Promise<ApiResponse<T[]>>;
  read?: (
    id: RequiredApiEntityId<T, EntityKey>,
    apis: ApiStoreFactoryApi,
  ) => Promise<T>;
  bulkRead?: (
    ids: RequiredApiEntityId<T, EntityKey>[],
    apis: ApiStoreFactoryApi,
  ) => Promise<T[]>;
  update?: (
    id: RequiredApiEntityId<T, EntityKey>,
    data: UpdateInput,
    apis: ApiStoreFactoryApi,
  ) => Promise<T>;
  discard?: (
    id: RequiredApiEntityId<T, EntityKey>,
    apis: ApiStoreFactoryApi,
  ) => Promise<void>;
  duplicate?: (
    id: RequiredApiEntityId<T, EntityKey>,
    apis: ApiStoreFactoryApi,
  ) => Promise<T>;
};

const wrapOperations = <
  T extends ApiEntity,
  TFilter extends GetFilter,
  EntityKey extends keyof T,
  CreateInput = T,
  UpdateInput = T,
>(
  operations: ApiCRUDOperations<
    T,
    TFilter,
    EntityKey,
    CreateInput,
    UpdateInput
  >,
): Required<
  ApiCRUDOperations<T, TFilter, EntityKey, CreateInput, UpdateInput>
> => {
  return {
    create: operations.create ?? notImplemented,
    readAll: operations.readAll ?? notImplemented,
    read: operations.read ?? notImplemented,
    bulkRead: operations.bulkRead ?? notImplemented,
    update: operations.update ?? notImplemented,
    discard: operations.discard ?? notImplemented,
    duplicate: operations.duplicate ?? notImplemented,
  };
};

type RemovePagination<T> = Omit<
  T,
  'count' | 'keysOnly' | 'total' | 'cursor' | 'offset'
>;

type Watcher<T extends ApiEntity> = {
  entity: WatchedEntity<T>;
  listenerCount: number;
};

// Unfortunately, TS does not parse EntityKey correctly and it's required to pass it explicitly in generic and as a parameter
export const apiStoreFactory = <
  T extends ApiEntity,
  EntityKey extends keyof T,
  TFilter extends GetFilter,
  CreateInput = T,
  UpdateInput = T,
>(
  storeId: STORE,
  {
    entityIdKey,
    supportPagination = true,
    debounceTimeout = 50,
  }: ApiStoreConfig<EntityKey>,
  optionalOperations: ApiCRUDOperations<
    T,
    TFilter,
    EntityKey,
    CreateInput,
    UpdateInput
  >,
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
) =>
  defineStore<
    STORE,
    ApiStoreSetup<T, TFilter, EntityKey, CreateInput, UpdateInput>
  >(
    storeId,
    // setup object looks better, but has a limitation of operations with context`
    // eslint-disable-next-line max-statements
    function ApiStoreFactorySetup(): ApiStoreSetup<
      T,
      TFilter,
      EntityKey,
      CreateInput,
      UpdateInput
    > {
      const log = logFactory.getChild(storeId);
      const apis = useApis();
      const pubSub = usePubSub();
      const factoryApi: ApiStoreFactoryApi = { ...apis, pubSub };

      log.log('Initializing store');

      pubSub.onUpdate(storeId, (key) => {
        const id = key as unknown as RequiredApiEntityId<T, EntityKey>;
        if (cache.value.has(id)) {
          log.log(`PubSub update id:`, id);
          // eslint-disable-next-line max-nested-callbacks
          void read(id, REQUEST_POLICY.NETWORK_ONLY).catch(() => {});
        }
      });

      pubSub.onDelete(storeId, (key) => {
        const id = key as unknown as RequiredApiEntityId<T, EntityKey>;
        log.log(`PubSub delete id:`, id);
        handleDiscard(id);
      });

      pubSub.onCacheUpdate(storeId, (id, value) => {
        const entity = value as T;
        log.log('Inner store cache update', id, entity);
        _updateCache(entity);
      });

      const operations = wrapOperations(optionalOperations);
      const filter = ref<RemovePagination<TFilter> | null>(
        null,
      ) as Ref<RemovePagination<TFilter> | null>;
      const cursor = ref<string | null>(null);
      const total = ref<number | null>(null);
      const page = ref<number>(0);
      const data = ref<T[]>([]) as Ref<T[]>;
      const loading = ref<boolean>(false);
      const error = ref<ResponseError | null>(
        null,
      ) as Ref<ResponseError | null>;
      const cache = ref(new Map() as Map<RequiredApiEntityId<T, EntityKey>, T>);
      const requestedKeys = new Set<RequiredApiEntityId<T, EntityKey>>();
      const readAllAbortController = ref<AbortController | null>(null);
      const hasEntities = ref<boolean>(false);

      const _setItemLoading = (
        id: RequiredApiEntityId<T, EntityKey>,
        loading: boolean | undefined = true,
      ) => {
        const watchedItem = watcherMemory.get(id);
        if (watchedItem) {
          log.debug(`Setting loading for item: ${id} to ${loading}`);
          watchedItem.entity.loading = loading;
        }
      };

      const _updateCache = (item: T): void => {
        if (!item[entityIdKey]) return;
        hasEntities.value = true;
        const id = item[entityIdKey] as RequiredApiEntityId<T, EntityKey>;

        cache.value.set(id, item);
        const watchedItem = watcherMemory.get(id);

        if (watchedItem) {
          log.debug('updating watched item', id);
          watchedItem.entity.item = item;
          watchedItem.entity.loading = false;
        }
      };

      const _updateData = (item: T) => {
        if (!item[entityIdKey]) return;
        for (let i = 0; i < data.value.length; i++) {
          if (data.value[i][entityIdKey] === item[entityIdKey]) {
            data.value[i] = item;
          }
        }

        _updateCache(item);
      };

      const _updateError = (
        newError: unknown,
        id?: RequiredApiEntityId<T, EntityKey>,
      ) => {
        if (!(newError instanceof ResponseError)) {
          const unknownError = newError as { message?: string };
          try {
            log.error(
              `Unexpected error type: ${
                unknownError.message ?? JSON.stringify(unknownError)
              }`,
            );
          } catch {
            log.error('Failed to stringify error', unknownError);
          }
          // API middleware MUST throw ResponseError
          // Every non-ResponseError is considered as unexpected
          // And must be reported to sentry
          return;
        }
        if (id) {
          const watchedItem = watcherMemory.get(id);
          if (watchedItem) {
            log.log(`updating watched item: ${id} error:`, newError);
            watchedItem.entity.error = newError;
            watchedItem.entity.loading = false;
          }
        } else {
          log.log('Updating error', newError);
          error.value = newError;
        }
      };

      const _filterFactory = (
        filter: RemovePagination<TFilter>,
        page = 0,
        requestTotal = false,
      ): TFilter => {
        if (!supportPagination) return filter as TFilter;

        const limitFromFilter = Number('limit' in filter ? filter.limit : '10');
        const limit = isNaN(limitFromFilter) ? 10 : limitFromFilter;
        const offset = page * limit;

        // we have indication that called API is Dialbunny API based 'supportPagination'
        // so we can safely assume that 'limit', 'offset' and 'total' are supported
        return {
          ...filter,
          limit,
          offset,
          // counting total number is expensive operation, so we can skip it if not needed
          ...(requestTotal ? { total: true } : {}),
        } as unknown as TFilter;
      };

      const _nextCursorPageFilterFactory = (
        filter: RemovePagination<TFilter>,
      ): TFilter | null => {
        if (!cursor.value) return null;

        // cursor was provided as answer from API, so we can safely assume that it's supported
        return {
          ...filter,
          cursor: cursor.value,
        } as unknown as TFilter;
      };

      const { withRetry: _readPageWithRetry, cancel: _readPageCancel } =
        exponentialRetry(
          // eslint-disable-next-line max-statements, complexity
          async (
            page: number,
            { silent = false, requestTotal = false, loadMore = false } = {},
            factory: (
              filter: RemovePagination<TFilter>,
              page: number,
              requestTotal: boolean,
            ) => TFilter | null = _filterFactory,
          ): Promise<void> => {
            log.log(
              `Reading page: ${page}, silent: ${silent}, filter:`,
              filter.value,
            );
            if (!filter.value) throw new Error('Filter is not set');
            if (readAllAbortController.value) {
              readAllAbortController.value.abort(ABORTED_ERROR);
            }

            const abortController = new AbortController();
            readAllAbortController.value = abortController;

            if (!silent) {
              log.debug('Setting loading to true');
              loading.value = true;
            }
            const newFilter = factory(
              filter.value,
              page,
              requestTotal || total.value === null,
            );
            if (!newFilter) {
              data.value = [];
              cursor.value = null;
              return;
            }
            try {
              const resp = await operations.readAll(
                newFilter,
                factoryApi,
                abortController.signal,
              );
              if (abortController.signal.aborted)
                throw new Error(ABORTED_ERROR);
              readAllAbortController.value = null;

              cursor.value = resp.raw.headers.get('cursor') ?? null;
              total.value = resp.raw.headers.get('total')
                ? Number(resp.raw.headers.get('total'))
                : total.value;
              const all = await resp.value();

              for (const item of all) {
                _updateCache(item);
              }

              if (!silent) {
                log.debug('Setting loading to false');
                loading.value = false;
              }

              data.value = loadMore ? [...data.value, ...all] : all;
            } catch (catchError) {
              if (
                // error that is thrown by API wrapper when operation is aborted
                // is a string, while we throw it manually as Error
                // so checking for both cases
                catchError === ABORTED_ERROR ||
                (catchError instanceof Error &&
                  catchError.message === ABORTED_ERROR)
              ) {
                log.debug('Operation was aborted');
                return;
              }
              if (isRetryError(catchError)) {
                log.debug(
                  'Received retryable error',
                  (catchError as ResponseError).response,
                );

                throw catchError;
              }

              loading.value = false;
              _updateError(catchError);
            }
          },
        );

      // every time there is a new filter we need to stop previous readPage and start new one
      const _readPage: typeof _readPageWithRetry = async (
        ...args: Parameters<typeof _readPageWithRetry>
      ): Promise<void> => {
        _readPageCancel();
        await _readPageWithRetry(...args);
      };

      const refresh = async (
        silent = true,
        requestTotal = false,
        resetHasEntities = false,
      ): Promise<void> => {
        log.log(`Refreshing data, silent: ${silent}`);
        if (!filter.value) throw new Error('Filter is not set');

        if (resetHasEntities) {
          hasEntities.value = false;
        }

        await _readPage(page.value, { silent, requestTotal });
      };

      const loadMore = async (): Promise<void> => {
        if (!cursor.value) {
          return;
        }
        log.log(`Loading more data, cursor: ${cursor.value}`);

        await _readPage(0, { loadMore: true }, _nextCursorPageFilterFactory);
      };

      const create = (
        obj: CreateInput,
        { onRetry, onError, onSuccess }: ResponseCallbacks<T> = {},
        fetchAll = true,
      ): CancelRetryCallback => {
        let canceled = false;
        const { withRetry, cancel } = exponentialRetry(
          async (): Promise<void> => {
            try {
              log.log(`Creating entity, fetchAll: ${fetchAll}`, obj);
              const created = await operations.create(obj, factoryApi);
              if (created[entityIdKey]) {
                _updateCache(created);
              }

              if (fetchAll && filter.value) {
                void refresh(true, true);
              }

              if (canceled) return;
              await onSuccess?.(created);
            } catch (catchError) {
              if (canceled) return;
              if (isRetryError(catchError)) {
                log.log(
                  'Recoverable error in create',
                  (catchError as ResponseError).response,
                );
                await onRetry?.(catchError as ResponseError);
                throw catchError;
              }

              log.warn(
                'Unrecoverable error in create',
                catchError,
                'response' in (catchError as ResponseError) &&
                  (catchError as ResponseError).response,
              );
              await onError?.(catchError);
            }
          },
        );

        void withRetry();

        return () => {
          canceled = true;
          cancel();
        };
      };

      const debounceRead = debounce(
        async (): Promise<Map<RequiredApiEntityId<T, EntityKey>, T>> => {
          // eslint-disable-next-line unicorn/prefer-spread
          const ids = Array.from(requestedKeys);
          log.log('Debounced read', ids);
          requestedKeys.clear();

          const map = new Map<RequiredApiEntityId<T, EntityKey>, T>();
          if (ids.length === 1) {
            const entity = await operations.read(ids[0], factoryApi);
            map.set(ids[0], entity);

            return map;
          }
          const entities = await operations.bulkRead(ids, factoryApi);

          for (const entity of entities) {
            map.set(
              entity[entityIdKey] as RequiredApiEntityId<T, EntityKey>,
              entity,
            );
          }

          return map;
        },
        debounceTimeout,
      );

      const { withRetry: combinedApiRead } = exponentialRetry(
        async (
          id: RequiredApiEntityId<T, EntityKey>,
          { onRetry, onError, onSuccess }: ResponseCallbacks<T> = {},
        ): Promise<T | undefined> => {
          try {
            log.log('Combined API read', id);
            requestedKeys.add(id);
            const debouncedResponse = await debounceRead();

            const result = debouncedResponse.get(id) as unknown;

            log.log('Combined API read result', id, result);
            if (!result) {
              throw new Error(
                `Combined API read: no result for ${entityIdKey.toString()}: ${id}`,
              );
            }

            await onSuccess?.(result as T);

            return result as T;
          } catch (catchError) {
            if (isRetryError(catchError)) {
              log.log(
                'Recoverable error in combined API read',
                (catchError as ResponseError).response,
              );
              await onRetry?.(catchError as ResponseError);
              throw catchError;
            }

            log.warn(
              'Unrecoverable error in combined API read',
              catchError,
              'response' in (catchError as ResponseError) &&
                (catchError as ResponseError).response,
            );
            await onError?.(catchError);
          }
        },
      );

      const promisifiedCombinedApiRead = (
        id: RequiredApiEntityId<T, EntityKey>,
      ): Promise<T> => {
        log.debug('Promisified combined API read', id);
        return new Promise((resolve, reject) => {
          void combinedApiRead(id, {
            onSuccess: resolve,
            onError: reject,
          });
        });
      };

      const read = async (
        id: RequiredApiEntityId<T, EntityKey>,
        requestPolicy: REQUEST_POLICY = REQUEST_POLICY.CACHE_FIRST,
      ): Promise<T> => {
        try {
          log.log(`Reading entity, id: ${id}, requestPolicy: ${requestPolicy}`);
          let item = cache.value.get(id);
          if (!item || requestPolicy === REQUEST_POLICY.NETWORK_ONLY) {
            item = await promisifiedCombinedApiRead(id);
            _updateData(item);
          } else if (requestPolicy === REQUEST_POLICY.CACHE_AND_NETWORK) {
            void promisifiedCombinedApiRead(id)
              .then((networkItem) => {
                _updateData(networkItem);
              })
              .catch((catchError) => {
                _updateError(catchError, id);
              });
          } else {
            // to update watched item
            log.debug('Found in cache', id);
            _updateCache(item);
          }

          return item;
        } catch (catchError) {
          _updateError(catchError, id);

          throw catchError;
        }
      };

      const update = (
        id: RequiredApiEntityId<T, EntityKey>,
        data: UpdateInput,
        { onRetry, onError, onSuccess }: ResponseCallbacks<T> = {},
      ): CancelRetryCallback => {
        let canceled = false;

        // defining inside function to have cancel per update
        const { withRetry, cancel } = exponentialRetry(
          async (): Promise<void> => {
            try {
              log.log(`Updating entity, id: ${id}`, data);
              _setItemLoading(id);
              const updated = await operations.update(id, data, factoryApi);
              _updateData(updated);
              if (canceled) return;
              await onSuccess?.(updated);
            } catch (catchError) {
              if (canceled) return;
              if (isRetryError(catchError)) {
                log.log(
                  'Retrying update, error:',
                  (catchError as ResponseError).response,
                );
                await onRetry?.(catchError as ResponseError);
                throw catchError;
              }
              await onError?.(catchError);
              log.warn(
                'Received non-recoverable error on update',
                catchError,
                'response' in (catchError as ResponseError) &&
                  (catchError as ResponseError).response,
              );
            }
          },
        );

        void withRetry();

        return () => {
          canceled = true;
          cancel();
        };
      };

      const handleDiscard = (id: RequiredApiEntityId<T, EntityKey>) => {
        log.log(`handleDiscard, id: ${id}`);
        cache.value.delete(id);
        data.value = data.value.filter((item) => item[entityIdKey] !== id);
        const watchedItem = watcherMemory.get(id);
        if (watchedItem) {
          watchedItem.entity.item = null;
          watchedItem.entity.loading = false;
        }
      };

      const discard = (
        id: RequiredApiEntityId<T, EntityKey>,
        { onRetry, onError, onSuccess }: ResponseCallbacks<void> = {},
      ): CancelRetryCallback => {
        let canceled = false;
        const { withRetry, cancel } = exponentialRetry(
          async (): Promise<void> => {
            try {
              log.log(`Discarding entity, id: ${id}`);
              _setItemLoading(id);
              await operations.discard(id, factoryApi);
              handleDiscard(id);
              if (filter.value) {
                void refresh(true, true);
              }
              if (canceled) return;
              await onSuccess?.();
            } catch (catchError) {
              if (canceled) return;
              if (isRetryError(catchError)) {
                log.log(
                  'Retrying discard, error:',
                  (catchError as ResponseError).response,
                );
                await onRetry?.(catchError as ResponseError);
                throw catchError;
              }
              await onError?.(catchError);
              log.warn(
                'Received non-recoverable error on discard',
                catchError,
                'response' in (catchError as ResponseError) &&
                  (catchError as ResponseError).response,
              );
            }
          },
        );

        void withRetry();

        return () => {
          canceled = true;
          cancel();
        };
      };

      const duplicate = (
        id: RequiredApiEntityId<T, EntityKey>,
        { onRetry, onError, onSuccess }: ResponseCallbacks<T> = {},
        fetchAll = true,
      ): CancelRetryCallback => {
        let canceled = false;

        const { withRetry, cancel } = exponentialRetry(
          async (): Promise<void> => {
            try {
              log.log(`Duplicating entity, id: ${id}, fetchAll: ${fetchAll}`);
              const duplicated = await operations.duplicate(id, factoryApi);
              if (duplicated[entityIdKey]) {
                _updateCache(duplicated);
              }

              if (fetchAll && filter.value) {
                void refresh();
              }

              if (canceled) return;
              await onSuccess?.(duplicated);
            } catch (catchError) {
              if (canceled) return;
              if (isRetryError(catchError)) {
                log.log(
                  'Retrying duplicate, error:',
                  (catchError as ResponseError).response,
                );
                await onRetry?.(catchError as ResponseError);
                throw catchError;
              }
              await onError?.(catchError);
              log.warn(
                'Received non-recoverable error on duplicate',
                catchError,
                'response' in (catchError as ResponseError) &&
                  (catchError as ResponseError).response,
              );
            }
          },
        );

        void withRetry();

        return () => {
          canceled = true;
          cancel();
        };
      };

      const clear = () => {
        log.log('Clearing store');
        data.value = [];
        filter.value = null;
        cursor.value = null;
        hasEntities.value = false;
        clearCache();
        clearError();
      };

      const clearError = () => {
        log.log('Clearing error');
        error.value = null;
      };

      const clearCache = (id?: RequiredApiEntityId<T, EntityKey>) => {
        log.log(`Clearing cache, id: ${id}`);
        if (id) {
          cache.value.delete(id);
        } else {
          cache.value.clear();
        }
      };

      const watcherMemory = new Map<
        RequiredApiEntityId<T, EntityKey>,
        Watcher<T>
      >();
      const removeWatcher = (id: RequiredApiEntityId<T, EntityKey>) => {
        log.log(`Removing watcher, id: ${id}`);
        const watchedItem = watcherMemory.get(id);
        if (!watchedItem) {
          log.warn('Unmount: watcher memory does not have id:', id);
          return;
        }

        const { entity, listenerCount } = watchedItem;
        if (listenerCount <= 1) {
          watcherMemory.delete(id);
          return;
        }

        watcherMemory.set(id, {
          entity,
          listenerCount: listenerCount - 1,
        });
      };
      const watchFunction: WatchFunction<T, EntityKey> = (
        getId: () => RequiredApiEntityId<T, EntityKey> | null,
        componentName: string,
      ) => {
        log.log(
          `Initializing watch function, ${componentName} component with initial id:`,
          getId(),
        );
        // value will be overwritten within synchronous watch action
        // it stays here just to avoid error
        const resultRef = ref<WatchedEntity<T>>({
          item: null,
          loading: false,
          error: null,
        }) as Ref<WatchedEntity<T>>;

        watch(
          getId,
          async (id, oldId) => {
            if (id === oldId) return;
            if (oldId) removeWatcher(oldId);
            if (oldId && !id) {
              resultRef.value = {
                item: null,
                loading: false,
                error: null,
              };
              return;
            }
            if (!id) return;
            log.log(
              `Watched id changed on ${componentName} component, new id: ${id}, old id: ${oldId}`,
            );

            const watcher =
              watcherMemory.get(id) ??
              ({
                entity: reactive({
                  item: null,
                  loading: false,
                  error: null,
                }),
                listenerCount: 0,
              } as Watcher<T>);

            const { listenerCount, entity } = watcher;

            resultRef.value = entity;

            log.debug(
              'Updating watched item',
              id,
              'with',
              listenerCount + 1,
              'on component',
              componentName,
            );
            watcherMemory.set(id, {
              entity,
              listenerCount: listenerCount + 1,
            });

            if (entity.item === null && !entity.loading) {
              entity.loading = true;
              log.log(
                'Reading item for watcher',
                id,
                'component',
                componentName,
              );
              void read(id).catch(() => {});
            }
          },
          { immediate: true },
        );

        onUnmounted(() => {
          const id = getId();
          log.log('Unmounting watcher, id:', id, 'from', componentName);
          if (!id) return;
          removeWatcher(id);
        });

        return resultRef;
      };

      const watchMany: WatchManyFunction<T, EntityKey> = (
        getIds,
        componentName,
      ) => {
        log.log(
          `Initializing watchMany function, ${componentName} component with initial ids:`,
          getIds(),
        );
        const resultRef = ref<WatchedEntity<T>[]>([]) as Ref<
          WatchedEntity<T>[]
        >;

        watch(
          getIds,
          // eslint-disable-next-line max-statements
          async (ids, oldIds) => {
            if (ids === oldIds) return;
            log.log(
              'Watched ids changed',
              componentName,
              'component, new ids:',
              ids,
              'old ids:',
              oldIds,
            );
            const newIds = new Set(ids);

            if (oldIds) {
              for (const id of oldIds) {
                if (!newIds.has(id)) {
                  removeWatcher(id);
                }
              }
            }

            const newWatchers: WatchedEntity<T>[] = [];
            for (const id of ids) {
              const watcher =
                watcherMemory.get(id) ??
                ({
                  entity: reactive({
                    item: null,
                    loading: false,
                    error: null,
                  }),
                  listenerCount: 0,
                } as Watcher<T>);

              const { listenerCount, entity } = watcher;

              newWatchers.push(entity);

              watcherMemory.set(id, {
                entity,
                listenerCount: listenerCount + 1,
              });

              if (entity.item === null && !entity.loading) {
                entity.loading = true;
                log.log(
                  'Reading item for watcher',
                  id,
                  'component',
                  componentName,
                );

                void read(id);
              }
            }

            resultRef.value = newWatchers;
          },
          { immediate: true },
        );

        onUnmounted(() => {
          for (const id of getIds()) {
            log.log(
              'Unmounting watcher, id:',
              id,
              'for component',
              componentName,
            );
            removeWatcher(id);
          }
        });

        return resultRef;
      };

      watch([filter, page], async (_, [prevFilter, prevPage]) => {
        if (!filter.value) return;

        clearError();
        if (
          filter.value === prevFilter ||
          JSON.stringify(filter.value) === JSON.stringify(prevFilter)
        ) {
          if (page.value === prevPage) {
            log.log('Filter and page did not change, skipping read');
            return;
          }

          log.log('Page changed, reading page:', page.value);
          await _readPage(page.value);
        } else {
          page.value = 0;
          total.value = null;

          log.log('Filter changed, refreshing data, new filter:', filter.value);
          await _readPage(0);
        }
      });

      return {
        // state
        filter,
        page,
        // getters
        data: computed(() => data.value),
        cursor: computed(() => cursor.value),
        loading: computed(() => loading.value),
        error: computed(() => error.value),
        cache: computed(() => cache.value),
        total: computed(() => total.value ?? 0),
        watcherMemory: computed(() => watcherMemory),
        hasEntities: computed(() => hasEntities.value),
        // actions
        refresh,
        loadMore,
        create,
        read,
        update,
        discard,
        duplicate,
        clear,
        clearError,
        clearCache,
        watch: watchFunction,
        watchMany,
      };
    },
  );
